import { authService } from "./authService";
import { API_BASE_URL } from "./config";

export interface ApiRequestOptions extends RequestInit {
  requiresAuth?: boolean;
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = "") {
    this.baseUrl = baseUrl;
  }

  private getHeaders(
    customHeaders: HeadersInit = {},
    requiresAuth: boolean = true
  ): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...customHeaders,
    };

    // Only add Authorization header if auth is required and token exists
    if (requiresAuth) {
      const token = authService.getToken();
      if (token) {
        (headers as Record<string, string>)[
          "Authorization"
        ] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    // Handle 401 Unauthorized - redirect to login
    if (response.status === 401) {
      authService.logout();
      throw new Error("Session expired. Please login again.");
    }

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch {
        // If response is not JSON, use default error message
      }

      throw new Error(errorMessage);
    }

    // Handle empty responses
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      return {} as T;
    }

    return response.json();
  }

  async get<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const { requiresAuth = true, ...requestOptions } = options;

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "GET",
      headers: this.getHeaders(requestOptions.headers, requiresAuth),
      ...requestOptions,
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const { requiresAuth = true, ...requestOptions } = options;

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "POST",
      headers: this.getHeaders(requestOptions.headers, requiresAuth),
      body: data ? JSON.stringify(data) : undefined,
      ...requestOptions,
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const { requiresAuth = true, ...requestOptions } = options;

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "PUT",
      headers: this.getHeaders(requestOptions.headers, requiresAuth),
      body: data ? JSON.stringify(data) : undefined,
      ...requestOptions,
    });

    return this.handleResponse<T>(response);
  }

  async patch<T>(
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const { requiresAuth = true, ...requestOptions } = options;

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "PATCH",
      headers: this.getHeaders(requestOptions.headers, requiresAuth),
      body: data ? JSON.stringify(data) : undefined,
      ...requestOptions,
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const { requiresAuth = true, ...requestOptions } = options;

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "DELETE",
      headers: this.getHeaders(requestOptions.headers, requiresAuth),
      ...requestOptions,
    });

    return this.handleResponse<T>(response);
  }
}

// Create default API client instance
export const apiClient = new ApiClient(API_BASE_URL);
